import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MultiSelectModule } from 'primeng/multiselect';

import { RoMasterService, RoMaster, RoMasterDto } from '../../services/ro-master';
import { ZoneMasterService, ZoneMaster } from '../../services/zone-master';
import { StateMasterService, StateMaster } from '../../services/state-master';
import { MasterPolicyService, MasterPolicy } from '../../services/master-policy';
import { ClientService, Client } from '../../services/client';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

interface Opt {
  label: string;
  value: number;
}

@Component({
  selector: 'app-regional-offices',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
    MultiSelectModule,
  ],
  templateUrl: './regional-offices.html',
  styleUrls: ['./regional-offices.css'],
})
export class RegionalOfficesPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(RoMasterService);
  private zoneSvc = inject(ZoneMasterService);
  private stateSvc = inject(StateMasterService);
  private policySvc = inject(MasterPolicyService);
  private clientSvc = inject(ClientService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  search = signal('');
  rows = signal<RoMaster[]>([]);

  // Lookups
  clients = signal<Client[]>([]);
  zones = signal<ZoneMaster[]>([]);
  states = signal<StateMaster[]>([]);
  /** Master policies for the client currently selected in the form (client-owned only). */
  policies = signal<MasterPolicy[]>([]);

  /** Optional list filter — view one client's ROs. 0 = all. */
  filterClientId = signal<number>(0);

  clientOptions = computed<Opt[]>(() =>
    this.clients().map((c) => ({ label: `${c.clientName} (${c.clientCode})`, value: c.id })),
  );
  clientFilterOptions = computed<Opt[]>(() => [{ label: 'All clients', value: 0 }, ...this.clientOptions()]);
  zoneOptions = computed<Opt[]>(() =>
    this.zones().filter((z) => z.isActive).map((z) => ({ label: z.zoneName, value: z.id })),
  );
  stateOptions = computed<Opt[]>(() =>
    this.states().filter((s) => s.isActive).map((s) => ({ label: s.stateName, value: s.id })),
  );
  policyOptions = computed<Opt[]>(() =>
    this.policies().map((p) => ({
      label: `${p.masterPolicyNumber} — ${p.masterPolicyName}`,
      value: p.id,
    })),
  );

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    const cid = this.filterClientId();
    return this.rows().filter((r) => {
      if (cid && r.clientId !== cid) return false;
      if (!q) return true;
      return (
        (r.roCode || '').toLowerCase().includes(q) ||
        (r.roName || '').toLowerCase().includes(q) ||
        (r.zoneName || '').toLowerCase().includes(q) ||
        (r.stateName || '').toLowerCase().includes(q) ||
        (r.stateNames || '').toLowerCase().includes(q) ||
        (r.masterPolicyNumber || '').toLowerCase().includes(q)
      );
    });
  });

  form: FormGroup = this.fb.group({
    id: [0],
    clientId: [null as number | null, Validators.required],
    roCode: ['', Validators.required],
    roName: ['', Validators.required],
    zoneId: [null as number | null],
    stateIds: [[] as number[]],   // an RO can span multiple states (many-to-many)
    masterPolicyId: [null as number | null],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    this.loadLookups();
    // When the client changes in the form, reload that client's master policies and
    // clear a now-invalid policy selection.
    this.form.get('clientId')?.valueChanges.subscribe((cid: number | null) => {
      this.form.get('masterPolicyId')?.setValue(null);
      this.policies.set([]);
      if (cid) this.loadPolicies(cid);
    });
  }

  load() {
    this.loading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  private loadLookups() {
    this.clientSvc.cltGetAll().subscribe({
      next: (res: any) => this.clients.set(Array.isArray(res?.data) ? res.data : []),
    });
    this.zoneSvc.getAll().subscribe({
      next: (res: any) => this.zones.set(Array.isArray(res?.data) ? res.data : []),
    });
    this.stateSvc.getAll().subscribe({
      next: (res: any) => this.states.set(Array.isArray(res?.data) ? res.data : []),
    });
  }

  /** Load client-owned master policies for the given client into the form dropdown. */
  private loadPolicies(clientId: number, thenSelect?: number | null) {
    this.policySvc.getAll({ ownerType: 'Client', ownerId: clientId }).subscribe({
      next: (res: any) => {
        this.policies.set(Array.isArray(res?.data) ? res.data : []);
        if (thenSelect != null) this.form.get('masterPolicyId')?.setValue(thenSelect);
      },
    });
  }

  openAdd() {
    this.editing.set(false);
    this.policies.set([]);
    this.form.reset({
      id: 0,
      clientId: null,
      roCode: '',
      roName: '',
      zoneId: null,
      stateIds: [],
      masterPolicyId: null,
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(r: RoMaster) {
    this.editing.set(true);
    // Patch WITHOUT firing the clientId subscription reset for masterPolicyId (we load & select it).
    this.form.reset({
      id: r.id,
      clientId: r.clientId,
      roCode: r.roCode ?? '',
      roName: r.roName ?? '',
      zoneId: r.zoneId ?? null,
      stateIds: r.stateIds?.length ? r.stateIds : (r.stateId ? [r.stateId] : []),
      masterPolicyId: null,
      isActive: r.isActive ?? true,
    });
    // Load this client's policies, then restore the saved policy selection.
    if (r.clientId) this.loadPolicies(r.clientId, r.masterPolicyId ?? null);
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  clientName(id: number): string {
    return this.clients().find((c) => c.id === id)?.clientName ?? '—';
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('clientId')?.invalid) this.toast.warning('Client is required.');
      else if (this.form.get('roCode')?.invalid) this.toast.warning('RO code is required.');
      else if (this.form.get('roName')?.invalid) this.toast.warning('RO name is required.');
      else this.toast.warning('Please complete the required fields.');
      return;
    }
    const v = this.form.getRawValue();
    const dto: RoMasterDto = {
      id: this.editing() ? Number(v.id) : 0,
      clientId: Number(v.clientId),
      roCode: (v.roCode || '').trim(),
      roName: (v.roName || '').trim(),
      zoneId: v.zoneId ?? null,
      stateIds: (v.stateIds ?? []) as number[],
      masterPolicyId: v.masterPolicyId ?? null,
      isActive: !!v.isActive,
    };
    this.saving.set(true);
    const req = this.editing() ? this.svc.update(dto) : this.svc.insert(dto);
    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save regional office');
          return;
        }
        this.toast.success(this.editing() ? 'Regional office updated' : 'Regional office created');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  toggleActive(r: RoMaster) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    this.svc.setActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<RoMaster>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async remove(r: RoMaster) {
    const ok = await this.toast.confirm(
      `"${r.roCode} — ${r.roName}" will be deleted.`,
      'Delete regional office?',
      'Delete',
    );
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('Regional office deleted');
        this.load();
      },
    });
  }
}
